import React, { CSSProperties, ReactNode, useMemo } from 'react';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { List, ListItem } from '@mui/material';
import LeftIcon from '~/assets/icons/jsx/LeftIcon';
import { COLORS } from '~/data/ui/colors';
import { StyledArrow, listItemsStyles, listStyles } from './useStyles';
import { Box } from '@mui/system';
import RESOURCES from '~/resources';
import { useIsUsedCar } from '~/hooks/useIsUsedCar';

type TBreadCrumbProps = {
  separator?: ReactNode;
  customStyles?: CSSProperties;
  manufacturer?: {
    he: string;
    en: string;
  };
  isUsedCarPage?: boolean;
};

const breadcrumbNameMap = {
  home: RESOURCES.BREADCRUMB_HOME,
  'new-car-for-sale': RESOURCES.BREADCRUMB_NEW_CAR,
  'private-leasing': RESOURCES.BREADCRUMB_LEASE,
  'used-car-for-sale': RESOURCES.BREADCRUMB_USED_CAR,
  listings: RESOURCES.BREADCRUMB_LISTINGS,
};

const Breadcrumb = ({
  separator,
  customStyles,
  manufacturer = {
    he: '',
    en: '',
  },
  isUsedCarPage = false,
}: TBreadCrumbProps) => {
  const paths = usePathname();
  const isUsedCar = useIsUsedCar();

  const pathNames = useMemo(() => {
    const pathsArray = paths.split('/');
    pathsArray.splice(2, 0, 'listings');

    const filteredPathsArray = pathsArray
      .filter((path) => path)
      .map((path, index, filteredArray) => {
        const decodedPath = decodeURIComponent(path);
        if (index === filteredArray.length - 1)
          return decodedPath.replace(/-.*/, '');

        return decodedPath;
      });

    return isUsedCar
      ? filteredPathsArray
      : filteredPathsArray.slice(0, filteredPathsArray.length - 1);
  }, [paths, isUsedCar]);

  return (
    <Box sx={customStyles} role="navigation" aria-label={RESOURCES.BREADCRUMB_NAVIGATION}>
      <List style={listStyles} disablePadding>
        <ListItem disablePadding sx={listItemsStyles}>
          <Link href={'/'}>{RESOURCES.BREADCRUMB_HOME}</Link>
        </ListItem>

        {pathNames.length > 0 &&
          (separator || (
            <LeftIcon color={COLORS.black.main} Component={StyledArrow} />
          ))}

        {pathNames.map((link, index) => {
          const isLastPathRepresentsCarYear =
            pathNames.length === index + 1 &&
            /^\d+$/.test(link) &&
            isUsedCarPage;

          const href = isLastPathRepresentsCarYear
            ? `/${pathNames
                .slice(0, 2)
                .join('/')}?minYear=${link}&maxYear=${link}`
            : `/${pathNames.slice(0, index + 1).join('/')}`;

          const manufacturerNameTransformer =
            link.toLowerCase() === manufacturer?.en.toLowerCase()
              ? manufacturer?.he || ''
              : '';

          const linkText =
            breadcrumbNameMap[link as keyof typeof breadcrumbNameMap] ||
            manufacturerNameTransformer ||
            link;

          return (
            <React.Fragment key={index}>
              <ListItem disablePadding sx={listItemsStyles}>
                {href && <Link href={href}>{linkText.toUpperCase()}</Link>}
              </ListItem>
              {pathNames.length !== index + 1 &&
                (separator || (
                  <LeftIcon color={COLORS.gray.dark} Component={StyledArrow} />
                ))}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default Breadcrumb;
